import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Timer, CalendarClock, ArrowRight } from 'lucide-react';
import api from '../../lib/api';

export const withdrawalTimerAPI = {
    get: () => api.get('/withdrawal-timer'),
    notify: () => api.post('/withdrawal-timer/notify'),
    issuer: () => api.get('/platform/issuer'),
};

export const useIssuerReference = () => {
    const [ref, setRef] = useState('');
    useEffect(() => { withdrawalTimerAPI.issuer().then((r) => setRef(r.data.issuer_reference)).catch(() => {}); }, []);
    return ref;
};

/* Dispara la notificación del temporizador N segundos después del login (una vez por sesión). */
export const WithdrawalTimerNotifier = () => {
    useEffect(() => {
        const loginAt = Number(sessionStorage.getItem('login_at') || 0);
        if (!loginAt || sessionStorage.getItem('withdrawal_timer_notified') === '1') return;
        let cancelled = false;
        let timeoutId;
        withdrawalTimerAPI.get().then(({ data }) => {
            if (cancelled || !data.enabled) return;
            const delay = Math.max(0, (data.notify_after_seconds || 120) * 1000 - (Date.now() - loginAt));
            timeoutId = setTimeout(async () => {
                try {
                    const { data: r } = await withdrawalTimerAPI.notify();
                    sessionStorage.setItem('withdrawal_timer_notified', '1');
                    if (r.ok && r.timer) {
                        toast(r.timer.title, { description: r.timer.message, duration: 15000, icon: <Timer className="w-4 h-4 text-amber-400" /> });
                        window.dispatchEvent(new Event('notifications:refresh'));
                    }
                } catch { /* silent */ }
            }, delay);
        }).catch(() => {});
        return () => { cancelled = true; clearTimeout(timeoutId); };
    }, []);
    return null;
};

export const WithdrawalTimerCard = () => {
    const [t, setT] = useState(null);
    const navigate = useNavigate();
    useEffect(() => { withdrawalTimerAPI.get().then((r) => setT(r.data)).catch(() => {}); }, []);
    if (!t || !t.enabled) return null;
    const tone = t.phase === 'active' ? 'border-amber-500/40 from-[#3a2a08] to-[#0f172a]' : t.phase === 'expired' ? 'border-slate-700 from-[#1e293b] to-[#0f172a]' : 'border-[#1973B8]/40 from-[#0b2a4a] to-[#0f172a]';
    const big = t.phase === 'active' ? `${t.remaining_days}` : t.phase === 'upcoming' ? `${t.remaining_days}` : '0';
    const bigLabel = t.phase === 'active' ? 'días restantes' : t.phase === 'upcoming' ? 'días para el inicio' : 'plazo finalizado';
    return (
        <div className={`rounded-2xl border bg-gradient-to-br ${tone} p-5 flex flex-wrap items-center justify-between gap-4`} data-testid="withdrawal-timer-card">
            <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-black/30 ring-1 ring-white/10 flex items-center justify-center flex-shrink-0">
                    <Timer className="w-5 h-5 text-amber-300" />
                </div>
                <div>
                    <p className="text-[10.5px] uppercase tracking-[0.14em] text-amber-300 font-bold">{t.title}</p>
                    <p className="text-white text-sm font-semibold mt-0.5 flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5 text-slate-400" /> {t.days} días naturales · {t.start_label} – {t.end_label}</p>
                    <p className="text-slate-400 text-[12px] mt-1" data-testid="withdrawal-timer-message">{t.message}</p>
                </div>
            </div>
            <div className="flex items-center gap-4">
                <div className="text-right">
                    <p className="text-4xl font-extrabold text-white tabular-nums leading-none" data-testid="withdrawal-timer-remaining">{big}</p>
                    <p className="text-[11px] uppercase tracking-wider text-slate-400 mt-1">{bigLabel}</p>
                </div>
                <button type="button" onClick={() => navigate('/withdraw')} className="h-10 px-4 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm font-semibold inline-flex items-center gap-1.5 transition-colors" data-testid="withdrawal-timer-cta">
                    Retiros <ArrowRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};
